import { Box, Button, Chip, Grid, IconButton, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../Redux/store'
import { Add, ArrowBackOutlined, ArrowForwardOutlined, DeleteOutlineOutlined, Description, EditOutlined, HowToReg, LocationPin, ReceiptLong, Remove, SettingsBackupRestore, VerifiedUser, Widgets } from '@mui/icons-material'
import { CartVariantImage, Item } from '../../../types/Cartlist'
import { ApiCreateOrder, ApiDeleteCartItem, ApiGetCartList, ApiUpdateCartItem } from '../../../Core/Apicall'
import { setActiveCartStep, setCartList, setSelectedDeliveryAddress, setSelectedBillingAddress, setBillingSameAsDelivery, setSelectedOrder } from '../../../Redux/Reducers/globalSlice'
import { toast } from 'react-toastify'
import { AddressInterface } from '../../../types/Address'


function OrderReview() {
    const { cartlist, activeCartStep, selectedDeliveryAddress, selectedBillingAddress, billingSameAsDelivery } = useSelector((state: RootState) => state.global)
    const dispatch = useDispatch()
    const billingAddress = billingSameAsDelivery ? selectedDeliveryAddress : selectedBillingAddress
    const [quantity, setQuantity] = useState(
        cartlist?.items?.map((item: Item) => ({
            quantity: item?.quantity,
            finalPrice: item?.finalPrice,
            total: item?.total,
        })) || []
    );

    useEffect(() => {
        setQuantity(cartlist?.items?.map((item: Item) => ({
            quantity: item?.quantity,
            finalPrice: item?.finalPrice,
            total: item?.total,
        })) || [])
    }, [cartlist])


    const GetCartlist = async () => {
        try {
            const res = await ApiGetCartList()
            if (res?.status) {
                dispatch(setCartList(res?.data))
            }
        } catch (error: any) {
            console.log("Error", error)
            toast.error(error?.message || "Something went wrong");
        }
    }

    const updateCartItem = async (cartItemId: number, type: "Add" | "Remove", index: number) => {
        try {
            const params = `?cartItemId=${cartItemId}`
            const data = {
                quantity: type === "Add" ? quantity?.[index]?.quantity + 1 : quantity?.[index]?.quantity - 1
            }
            const res = await ApiUpdateCartItem(params, data)
            if (res?.status) {
                GetCartlist()
            }
        } catch (error: any) {
            console.log("Error", error)
            toast.error(error?.message || "Something went wrong");
        }
    }

    const removeCartItem = async (cartItemId: number) => {
        try {
            const params = `?cartItemId=${cartItemId}`
            const res = await ApiDeleteCartItem(params)
            if (res?.status) {
                GetCartlist()
            }
        } catch (error: any) {
            console.log("Error", error)
            toast.error(error?.message || "Something went wrong");
        }
    }
    const PlaceOrder = async () => {
        if (!selectedDeliveryAddress?.addressId) {
            toast.error("Please select a delivery address")
            dispatch(setActiveCartStep(1))
            return
        }
        if (!billingAddress?.addressId) {
            toast.error("Please select a billing address")
            dispatch(setActiveCartStep(1))
            return
        }
        try {
            const data = {
                "cartId": cartlist?.cartId,
                "shippingAddressId": selectedDeliveryAddress.addressId,
                "billingAddressId": billingAddress.addressId,
                "isBillingSameAsShipping": billingSameAsDelivery,
            }
            const res = await ApiCreateOrder(data)
            if (res?.status) {
                dispatch(setSelectedOrder(res?.data))
                dispatch(setActiveCartStep(activeCartStep + 1))
                GetCartlist()
                dispatch(setSelectedDeliveryAddress(null))
                dispatch(setSelectedBillingAddress(null))
                dispatch(setBillingSameAsDelivery(true))
            }
        } catch (error: any) {
            toast.error(error?.message || "Something went wrong")
        }
    }

    const getProceedPreviousButton = () => {
        return (
            <Box className='d-flex align-items-center justify-content-start mt-3'>
                <Button className='btn-outlined-main px-3 py-1' onClick={() => { dispatch(setActiveCartStep(activeCartStep - 1)) }}><ArrowBackOutlined />&nbsp;Previous</Button>
            </Box>
        )
    }
    return (
        <Box>
            <Box>
                <Typography variant='body2' className='text-main fw-bold' gutterBottom>step 3 of 4</Typography>
                <Typography variant='h5' className='fw-bold'>Review Your Order</Typography>
                <Typography variant='body2' className='text-grey' sx={{ letterSpacing: '1px' }}>Review your order details before placing it.</Typography>
            </Box>
            <Grid className='mt-3' container spacing={1} sx={{ alignItems: 'start' }}>
                <Grid size={{ xs: 12, md: 'grow' }}>
                    <Box className='border bg-white border-10 p-3'>
                        <Box className='d-flex align-items-center gap-2 mb-2'>
                            <Widgets className='text-dark-main' />
                            <Typography variant='h6' className='fw-bold'>Order Items ({cartlist?.items?.length})</Typography>
                        </Box>
                        <Box className='mt-3'>
                            {cartlist?.items?.map((item: Item, index: number) => (
                                <React.Fragment key={index}>
                                    <Box key={index} className='d-flex flex-column flex-md-row align-items-center justify-content-between mb-2'>
                                        <Box className='d-flex align-items-center' sx={{ gap: '10px' }}>
                                            <Box component={'img'} height={'75px'} width={'75px'} className='border-10 border' src={item?.variant?.images?.find((image: CartVariantImage) => image?.is_thumbnail)?.imageUrl || item?.variant?.images?.[0]?.imageUrl} alt={`Item ${index}`} />
                                            <Box>
                                                <Typography variant='body2' className='fw-bold' gutterBottom>{item?.variant?.name}</Typography>
                                                <Box className='d-flex align-items-center gap-3'>
                                                    <Typography variant='caption' gutterBottom>Size: {item?.variant?.size?.name}</Typography>
                                                    <Typography variant='caption' gutterBottom>Color: {item?.variant?.color?.name}</Typography>
                                                </Box>
                                                <Box className='d-flex align-items-end gap-1'>
                                                    <Typography variant='body2' className='fw-bold' gutterBottom>
                                                        {Number(item?.finalPrice).toLocaleString("en-US", {
                                                            style: "currency",
                                                            currency: "INR",
                                                        })}
                                                    </Typography>
                                                    <Typography variant='body2' className='fw-bold over-line' gutterBottom>
                                                        {Number(item?.price).toLocaleString("en-US", {
                                                            style: "currency",
                                                            currency: "INR",
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box className='d-flex align-items-center justify-content-between' sx={{ width: { xs: "100%", md: 'auto' } }}>
                                            <Typography variant='h6' className='fw-bold d-inline-block d-md-none'>
                                                {Number(quantity?.[index]?.total).toLocaleString("en-US", {
                                                    style: "currency",
                                                    currency: "INR",
                                                })}
                                            </Typography>
                                            <Box className='d-flex align-items-center gap-3'>
                                                <Box sx={{ width: 'fit-content' }} className='d-flex align-items-center border border-10 border-main overflow-hidden'>
                                                    <IconButton
                                                        onClick={() => updateCartItem(item?.cartItemId, "Remove", index)}
                                                        disabled={quantity?.[index]?.quantity === 1}
                                                        sx={{ borderRadius: 0 }}
                                                    >
                                                        <Remove />
                                                    </IconButton>

                                                    <Typography className=' text-center fw-bold mx-2'
                                                    >
                                                        {quantity?.[index]?.quantity}
                                                    </Typography>
                                                    <IconButton
                                                        disabled={quantity?.[index]?.quantity === (Number(item?.variant?.availableStock) - 1)}
                                                        onClick={() => updateCartItem(item?.cartItemId, "Add", index)}
                                                        sx={{ borderRadius: 0 }}
                                                    >
                                                        <Add />
                                                    </IconButton>
                                                </Box>
                                                <Typography variant='body1' className='fw-bold d-md-inline-block d-none'>
                                                    {Number(quantity?.[index]?.total).toLocaleString("en-US", {
                                                        style: "currency",
                                                        currency: "INR",
                                                    })}
                                                </Typography>
                                                <IconButton onClick={() => { removeCartItem(item?.cartItemId) }}>
                                                    <DeleteOutlineOutlined className='text-danger' />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <hr />
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>
                    <Box className='border bg-white border-10 mt-2 p-3'>
                        <Box className='d-flex align-items-center justify-content-between mb-2'>
                            <Box className='d-flex align-items-center gap-2'>
                                <LocationPin className='text-dark-main' />
                                <Typography variant='h6' className='fw-bold'>Delivery Address</Typography>
                            </Box>
                            <IconButton className='m-0 p-0' onClick={(e) => { e.stopPropagation(); dispatch(setActiveCartStep(1)) }}><EditOutlined className='text-main' sx={{ fontSize: { xs: "24px", md: '18px' } }} /> &nbsp;<Typography variant='body2' className='d-none d-md-inline-block text-main'>Change Address</Typography></IconButton>
                        </Box>
                        <Box className='mt-3'>
                            <Box className='mb-2 border-10 p-3 d-flex align-items-start justify-content-between gap-3'
                                sx={{
                                    cursor: 'pointer',
                                    backgroundColor: 'var(--primary-light-color)',
                                    border: '1px solid var(--primary-color)'
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Box className='d-flex align-items-center gap-2 mb-2'>
                                        <Typography variant='body1' className='fw-bold'>{selectedDeliveryAddress?.contactPerson}</Typography>
                                        {selectedDeliveryAddress?.isDefault && (<Chip
                                            label="Default"
                                            className='m-0 text-white'
                                            sx={{ height: 'auto', background: "var(--primary-dark-color)", padding: '2px 3px' }}
                                        ></Chip>)}
                                    </Box>
                                    <Typography variant='body2' className='text-grey'>{selectedDeliveryAddress?.addressLine1}</Typography>
                                    <Typography variant='body2' className='text-grey'>{selectedDeliveryAddress?.addressLine2}</Typography>
                                    <Typography variant='body2' className='text-grey'>{selectedDeliveryAddress?.city},{selectedDeliveryAddress?.state}-{selectedDeliveryAddress?.postalCode},{selectedDeliveryAddress?.country}</Typography>
                                    <Typography variant='body2' className='text-grey'>{selectedDeliveryAddress?.mobileNumber}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box className='border bg-white border-10 mt-2 p-3'>
                        <Box className='d-flex align-items-center justify-content-between mb-2'>
                            <Box className='d-flex align-items-center gap-2'>
                                <ReceiptLong className='text-dark-main' />
                                <Typography variant='h6' className='fw-bold'>Billing Address</Typography>
                                {billingSameAsDelivery && (<Chip
                                    label="Same as delivery"
                                    className='m-0 text-white'
                                    sx={{ height: 'auto', background: "var(--primary-dark-color)", padding: '2px 3px' }}
                                ></Chip>)}
                            </Box>
                            <IconButton className='m-0 p-0' onClick={(e) => { e.stopPropagation(); dispatch(setActiveCartStep(1)) }}><EditOutlined className='text-main' sx={{ fontSize: { xs: "24px", md: '18px' } }} /> &nbsp;<Typography variant='body2' className='d-none d-md-inline-block text-main'>Change Address</Typography></IconButton>
                        </Box>
                        <Box className='mt-3'>
                            <Box className='mb-2 border-10 p-3 d-flex align-items-start justify-content-between gap-3'
                                sx={{
                                    backgroundColor: 'var(--primary-light-color)',
                                    border: '1px solid var(--primary-color)'
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Box className='d-flex align-items-center gap-2 mb-2'>
                                        <Typography variant='body1' className='fw-bold'>{billingAddress?.contactPerson}</Typography>
                                        {billingAddress?.isDefault && (<Chip
                                            label="Default"
                                            className='m-0 text-white'
                                            sx={{ height: 'auto', background: "var(--primary-dark-color)", padding: '2px 3px' }}
                                        ></Chip>)}
                                    </Box>
                                    <Typography variant='body2' className='text-grey'>{billingAddress?.addressLine1}</Typography>
                                    <Typography variant='body2' className='text-grey'>{billingAddress?.addressLine2}</Typography>
                                    <Typography variant='body2' className='text-grey'>{billingAddress?.city},{billingAddress?.state}-{billingAddress?.postalCode},{billingAddress?.country}</Typography>
                                    <Typography variant='body2' className='text-grey'>{billingAddress?.mobileNumber}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box className='border bg-white border-10 mt-2 p-3'>
                        <Box className='d-flex align-items-center gap-2 mb-2'>
                            <Description className='text-dark-main' />
                            <Typography variant='h6' className='fw-bold'>Order Summary</Typography>
                        </Box>
                        <Box className='d-flex align-items-center justify-content-between gap-5 mb-2'>
                            <Typography className='text-grey' variant='body2'>Subtotal ({cartlist?.items?.length} Items)</Typography>
                            <Typography variant='body2'>
                                {Number(cartlist?.subtotal).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "INR",
                                })}
                            </Typography>
                        </Box>
                        <Box className='d-flex align-items-center justify-content-between gap-5 mb-2'>
                            <Typography className='text-grey' variant='body2'>Tax({cartlist?.taxPercentage}%)</Typography>
                            <Typography variant='body2'>
                                {Number(cartlist?.taxAmount).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "INR",
                                })}
                            </Typography>
                        </Box>
                        <Box className='d-flex align-items-center justify-content-between gap-5 mb-2'>
                            <Typography className='text-grey' variant='body2'>Shipping Amount</Typography>
                            <Typography variant='body2'>
                                {Number(cartlist?.shippingAmount).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "INR",
                                })}
                            </Typography>
                        </Box>
                        <Box className='d-flex align-items-center justify-content-between gap-5'>
                            <Typography className='text-grey' variant='body2'>Total Discount</Typography>
                            <Typography className='text-green' variant='body2'>
                                {Number(cartlist?.totalDiscount).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "INR",
                                })}
                            </Typography>
                        </Box>
                        <hr />
                        <Box className='d-flex align-items-center justify-content-between gap-5 mt-2'>
                            <Typography className='text-grey' variant='body1'>Total Amount</Typography>
                            <Typography className='text-dark-main fw-bold' variant='h6'>
                                {Number(cartlist?.grandTotal).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "INR",
                                })}
                            </Typography>
                        </Box>
                    </Box>
                    {getProceedPreviousButton()}
                </Grid>
                <Grid size={{ xs: 12, md: 3, xl: 3 }} className='border bg-white border-10 p-3'>
                    <Box>
                        <Box className='d-flex align-items-center gap-3 mb-3'>
                            <Box className='p-2 bg-second-light border-10'>
                                <VerifiedUser className='text-main' />
                            </Box>
                            <Box>
                                <Typography variant='body2' className='fw-bold'>Secure & Safe Checkout</Typography>
                                <Typography variant='caption'>Your information is protected</Typography>
                            </Box>
                        </Box>
                        <Box className='d-flex align-items-center gap-3 mb-3'>
                            <Box className='p-2 bg-second-light border-10'>
                                <HowToReg className='text-main' />
                            </Box>
                            <Box>
                                <Typography variant='body2' className='fw-bold'>Trusted Seller</Typography>
                                <Typography variant='caption'>Official brand products</Typography>
                            </Box>
                        </Box>
                        <Box className='d-flex align-items-center gap-3'>
                            <Box className='p-2 bg-second-light border-10'>
                                <SettingsBackupRestore className='text-main' />
                            </Box>
                            <Box>
                                <Typography variant='body2' className='fw-bold'>Easy Returns</Typography>
                                <Typography variant='caption'>Hassle-free returns</Typography>
                            </Box>
                        </Box>
                    </Box>
                    <hr />
                    <Button onClick={() => { PlaceOrder(); }} fullWidth className='btn-main px-3 py-2'>Place Order&nbsp;<ArrowForwardOutlined /></Button>
                </Grid>
            </Grid>
        </Box>
    )
}

export default OrderReview